/** A banner on the board for anyone whose Plan My Week emails are on but can never arrive.
 *
 *  The same warning inside "My emails" was not enough: it lives on the Plan My Week tab, and the
 *  managers it is for spend their time on the board. One went three weeks with both emails ticked
 *  and nothing sent. Dismissing hides it for this browser session only; it is back next visit
 *  until reps are picked.
 */
import React, { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import { emailsSendNothing } from './salesPlanEmailState';

const DISMISS_KEY = 'sift:sales-view:emailNudgeDismissed';

interface SalesEmailNudgeProps {
  impersonateRole?: string;
  /** Changes whenever the board is shown again, so picking reps on the plan tab clears it. */
  checkToken: unknown;
  onPickReps: () => void;
}

export const SalesEmailNudge: React.FC<SalesEmailNudgeProps> = ({
  impersonateRole, checkToken, onPickReps,
}) => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (dismissed) return;
    const controller = new AbortController();
    siftApiClient.getSalesPlanSettings({ impersonate_role: impersonateRole }, controller.signal)
      .then((settings) => setShow(emailsSendNothing(settings)))
      .catch(() => {
        // A failed check is not a reason to nag; the plan tab still says it.
      });
    return () => controller.abort();
  }, [impersonateRole, checkToken, dismissed]);

  if (!show || dismissed) return null;
  return (
    <div className="sv-email-nudge" role="alert">
      <span>
        <strong>Your Plan My Week emails are on, but nothing is being sent.</strong>{' '}
        You don&apos;t have a territory of your own, so pick the reps whose weeks you want.
      </span>
      <button type="button" className="sv-email-nudge-btn" onClick={onPickReps}>
        Pick reps
      </button>
      <button
        type="button"
        className="sv-email-nudge-close"
        aria-label="Dismiss for now"
        onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
      >
        ×
      </button>
    </div>
  );
};
